import { Building2, Lock, Sparkles } from 'lucide-react';
import { PageHeader } from '@gs/ui';
import { useAuth } from '@gs/auth';
import { ALL_APPS, COMING_APPS } from '../data/apps';
import { adminZoneApps, funZoneApps, gsZoneApps, isAppOpenable } from '../data/catalogVisibility';
import { useFavorites } from '../hooks/useFavorites';
import { useMe } from '../hooks/useMe';
import { tuilesAdministration } from '../lib/administrationTiles';
import { AppCard } from '../components/AppCard';
import { ZoneHeader } from '../components/ZoneHeader';
import { PwaInstallButton } from '../components/PwaInstallButton';
import { useTranslation } from '../i18n';

const GRID = 'grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(80px,96px))]';

export default function AppsPage() {
  const { t } = useTranslation();
  const { toggle, isFav } = useFavorites();
  const { user } = useAuth();
  // La zone d'administration suit le ROLE EN BASE (verdict de `/api/me`, meme primitive que la
  // Console UG), plus un groupe Entra — voir src/lib/administrationTiles.ts. Affichage seulement.
  const me = useMe(!!user);

  const gsApps = gsZoneApps(ALL_APPS);
  const adminTiles = tuilesAdministration(adminZoneApps(ALL_APPS), me.data?.administration);
  const funApps = funZoneApps(ALL_APPS);
  // Le survol d'une tuile dit deja pourquoi elle est grise ; cette note le dit
  // sans survol, pour qui parcourt l'ecran des yeux (et sur telephone, ou il
  // n'y a pas de survol du tout).
  const gsHasGreyed = gsApps.some((a) => !isAppOpenable(a));

  return (
    <div className="space-y-8 pb-10">
      <PageHeader
        title={t('hub:catalogPage.title')}
        description={t('hub:catalogPage.description')}
        actions={<PwaInstallButton />}
      />

      {/* ── Zone 1 : Applications GS ──────────────────────────────── */}
      <section aria-label={t('hub:catalogPage.gsZone.aria')}>
        <ZoneHeader
          icon={<Building2 size={16} />}
          title={t('hub:catalogPage.gsZone.title')}
          subtitle={t('hub:catalogPage.gsZone.subtitle')}
          accentColor="brand"
          badge={t('hub:catalogPage.gsZone.badge', { n: gsApps.length })}
        />
        <div className={GRID}>
          {gsApps.map((app, i) => (
            <AppCard
              key={app.id}
              app={app}
              showStar
              isFav={isFav(app.id)}
              onToggleFav={() => toggle(app.id)}
              index={i}
              userEmail={user?.email}
            />
          ))}
        </div>

        {gsHasGreyed && (
          <p className="mt-3 text-meta text-slate-400 dark:text-slate-500">
            {t('hub:catalogPage.gsZone.greyedFootnote')}
          </p>
        )}

        {/* Bientôt disponible */}
        {COMING_APPS.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {COMING_APPS.map((c) => (
              <span
                key={c.id}
                className="rounded-full border border-dashed border-slate-300 bg-slate-50 px-3 py-1.5 text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-400"
                title={t(`hub:coming.${c.id}.desc`)}
              >
                {t(`hub:coming.${c.id}.name`)}
              </span>
            ))}
            <span className="flex items-center px-1 text-xs text-slate-400 dark:text-slate-500">
              {t('hub:catalogPage.comingSoon')}
            </span>
          </div>
        )}
      </section>

      {/* ── Zone 2 : Administration (selon le role en base) ──────── */}
      {adminTiles.length > 0 && (
        <section aria-label={t('hub:catalogPage.adminZone.aria')}>
          <ZoneHeader
            icon={<Lock size={14} />}
            title={t('hub:catalogPage.adminZone.title')}
            subtitle={t('hub:catalogPage.adminZone.subtitle')}
            accentColor="blue"
          />
          <div className={GRID}>
            {adminTiles.map((tuile, i) => (
              <AppCard
                key={tuile.key}
                app={tuile.app}
                showStar
                isFav={isFav(tuile.app.id)}
                onToggleFav={() => toggle(tuile.app.id)}
                index={i}
                userEmail={user?.email}
                nameOverride={tuile.compagnie ? t('hub:catalog.ugAdministration.name', { company: tuile.compagnie }) : undefined}
                descOverride={tuile.compagnie ? t('hub:catalog.ugAdministration.desc', { company: tuile.compagnie }) : undefined}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Zone 3 : Espace GS ─────────────────────────────────── */}
      <section aria-label={t('hub:catalogPage.funZone.aria')}>
        <ZoneHeader
          icon={<Sparkles size={15} />}
          title={t('hub:catalogPage.funZone.title')}
          subtitle={t('hub:catalogPage.funZone.subtitle')}
          accentColor="violet"
          badge={t('hub:catalogPage.funZone.badge')}
        />
        <div className={GRID}>
          {funApps.map((app, i) => (
            <AppCard
              key={app.id}
              app={app}
              showStar
              isFav={isFav(app.id)}
              onToggleFav={() => toggle(app.id)}
              index={i}
              userEmail={user?.email}
            />
          ))}
        </div>
        <p className="mt-3 text-meta text-slate-400 dark:text-slate-500">
          {t('hub:catalogPage.funZone.footnote')}
        </p>
      </section>
    </div>
  );
}
